import { z } from 'zod';
import { countryForRegion, schoolRegions } from 'constants/schoolRegions';
import { isValidCoordinates } from './timezones';

export const COORDINATE_PAIR_ERROR =
  'Paste latitude, longitude with latitude between -90 and 90 and longitude between -180 and 180.';

export const parseCoordinatePair = (text: string) => {
  let value = text.trim();
  if (value.startsWith('(') && value.endsWith(')')) value = value.slice(1, -1);
  const match = value.match(
    /^\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*,\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*$/,
  );
  if (!match) return undefined;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  return isValidCoordinates(lat, lng) ? { lat, lng } : undefined;
};

export type SchoolLocation = {
  country?: 'US' | 'CA';
  state?: string;
  address?: string;
};

export const isEmptyLocationValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === 0 ||
  value === '0' ||
  (typeof value === 'string' && value.trim() === '');

export const missingLocationFields = (
  current: { country?: unknown; state?: unknown; address?: unknown },
  location: SchoolLocation,
  countryIsDefault = false,
): SchoolLocation => {
  const updates: SchoolLocation = {};
  if (isEmptyLocationValue(current.country) || countryIsDefault) {
    const country =
      typeof current.state === 'string' && schoolRegions[current.state]
        ? countryForRegion(current.state)
        : location.country;
    if (country) updates.country = country;
  }
  const country = updates.country ?? current.country;
  if (
    isEmptyLocationValue(current.state) &&
    location.state &&
    countryForRegion(location.state) === country
  )
    updates.state = location.state;
  if (
    'address' in current &&
    isEmptyLocationValue(current.address) &&
    location.address
  )
    updates.address = location.address;
  return updates;
};

const geocodeResultsSchema = z.array(
  z.object({
    formatted_address: z.string().optional(),
    address_components: z.array(
      z.object({ short_name: z.string(), types: z.array(z.string()) }),
    ),
  }),
);

type MapsBrowser = {
  google?: {
    maps?: {
      Geocoder?: new () => {
        geocode: (
          request: { location: { lat: number; lng: number } },
          callback: (results: unknown, status: string) => void,
        ) => void;
      };
    };
  };
};

export const getSchoolLocation = async (
  lat: number,
  lng: number,
): Promise<SchoolLocation> => {
  if (!isValidCoordinates(lat, lng))
    throw new Error('Enter valid coordinates.');
  // Use the Maps browser service already loaded for address autocomplete.
  // Its HTTP-referrer-restricted key cannot call the REST geocoding endpoint.
  const Geocoder = (window as unknown as MapsBrowser).google?.maps?.Geocoder;
  if (!Geocoder)
    throw new Error(
      'Location lookup is unavailable. Enter the location fields manually or retry.',
    );
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const response = await new Promise<unknown>((resolve, reject) => {
      timeout = setTimeout(() => reject(new Error('Lookup timed out')), 10000);
      new Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK') resolve(results);
        else reject(new Error('No location found'));
      });
    });
    const results = geocodeResultsSchema.parse(response);
    if (!results.length) throw new Error('No location found');
    const components = results.flatMap((item) => item.address_components);
    const country = components.find((part) =>
      part.types.includes('country'),
    )?.short_name;
    if (country !== 'US' && country !== 'CA') return {};
    const state = components.find((part) =>
      part.types.includes('administrative_area_level_1'),
    )?.short_name;
    return {
      country,
      state:
        state && schoolRegions[state] && countryForRegion(state) === country
          ? state
          : undefined,
      address: results[0].formatted_address,
    };
  } catch {
    throw new Error(
      'Could not look up this location. Enter the location fields manually or retry.',
    );
  } finally {
    clearTimeout(timeout);
  }
};
